import { z } from 'zod';
import { moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';
import { UPLOAD_CONTENT_TYPES } from './upload.schemas';

/** Expenses (gitaumoses4/chukua#62) and their approval (#114): `submitted` is the pending state, and one decision (approved, rejected, exception) ends it. */

/** The categories the migration seeds. They are rows (`expense_categories`), so a deployment may hold others; these are the starter codes only. */
export const STARTER_EXPENSE_CATEGORY_CODES = ['fuel', 'toll', 'parking', 'weighbridge', 'small_repair', 'other'] as const;

/** Derived from an expense's latest transition, never stored on the expense. */
export const EXPENSE_STATES = ['submitted', 'approved', 'rejected', 'exception'] as const;
export type ExpenseState = (typeof EXPENSE_STATES)[number];

export const EXPENSE_RECEIPT_KINDS = ['receipt', 'pump_photo'] as const;
export type ExpenseReceiptKind = (typeof EXPENSE_RECEIPT_KINDS)[number];

/** Photos of paper: the same types and ceiling as a trip photo, so the phone has one camera path. */
export const EXPENSE_RECEIPT_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const satisfies readonly (typeof UPLOAD_CONTENT_TYPES)[number][];
export const MAX_EXPENSE_RECEIPT_BYTES = 10 * 1024 * 1024;

export const expenseCategoryCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'A category code is lower case letters, digits and underscores');

/** What a fuel purchase carries on top of an amount. The pump photo is a receipt of kind `pump_photo`. */
export const expenseFuelDetailsSchema = z.object({
  litres: z
    .number()
    .positive()
    .max(99_999.99)
    .refine((litres) => Math.round(litres * 100) / 100 === litres, 'Litres have at most two decimals'),
  station: z.string().trim().min(1).max(120),
  odometerKm: z.number().int().min(0).max(9_999_999),
});
export type ExpenseFuelDetails = z.infer<typeof expenseFuelDetailsSchema>;

export const positiveMoneySchema = moneySchema.refine((money) => money.amountMinor > 0, {
  message: 'An expense must be more than zero',
  path: ['amountMinor'],
});

/** A cost belongs to a trip or to a truck (a non-trip cost), never both and never neither. */
export function hasExactlyOneSubject(expense: { tripId?: string | null; truckId?: string | null }): boolean {
  return Number(Boolean(expense.tripId)) + Number(Boolean(expense.truckId)) === 1;
}
export const EXPENSE_SUBJECT_MESSAGE = 'Give a trip or a truck, not both and not neither';

/** `POST /expenses`: the office captures an expense for a driver. `capturedAt` is when it happened (the device time); it defaults to now. */
export const createExpenseInputSchema = z
  .object({
    driverId: z.uuid(),
    tripId: z.uuid().optional(),
    truckId: z.uuid().optional(),
    categoryCode: expenseCategoryCodeSchema,
    amount: positiveMoneySchema,
    note: z.string().trim().max(500).optional(),
    fuel: expenseFuelDetailsSchema.optional(),
    capturedAt: z.iso.datetime({ offset: true }).optional(),
  })
  .refine(hasExactlyOneSubject, { message: EXPENSE_SUBJECT_MESSAGE, path: ['tripId'] });
export type CreateExpenseInput = z.output<typeof createExpenseInputSchema>;

/** `POST /expenses/:id/receipts/presign`. */
export const expenseReceiptPresignRequestSchema = z.object({
  kind: z.enum(EXPENSE_RECEIPT_KINDS).default('receipt'),
  contentType: z.enum(EXPENSE_RECEIPT_CONTENT_TYPES),
  sizeBytes: z.number().int().positive().max(MAX_EXPENSE_RECEIPT_BYTES),
});
export type ExpenseReceiptPresignRequest = z.output<typeof expenseReceiptPresignRequestSchema>;

/** A presigned POST: send the file as multipart form data to `url`, `fields` first. */
export const expenseReceiptPresignResponseSchema = z.object({
  objectKey: z.string(),
  url: z.string(),
  fields: z.record(z.string(), z.string()),
  expiresAt: z.iso.datetime(),
});
export type ExpenseReceiptPresignResponse = z.infer<typeof expenseReceiptPresignResponseSchema>;

/** `POST /expenses/:id/receipts/confirm`: the upload to `objectKey` has finished. Storage is checked, not trusted. */
export const expenseReceiptConfirmRequestSchema = z.object({
  kind: z.enum(EXPENSE_RECEIPT_KINDS).default('receipt'),
  objectKey: z.string().min(1).max(512),
  /** The phone's own id for the photo: confirming it twice returns one row even under a new `objectKey`. */
  clientMediaId: z.uuid().optional(),
});
export type ExpenseReceiptConfirmRequest = z.output<typeof expenseReceiptConfirmRequestSchema>;

export const expenseReceiptConfirmResponseSchema = z.object({
  receiptId: z.uuid(),
  objectKey: z.string(),
  sizeBytes: z.number().int(),
  /** True when this object (or this `clientMediaId`) was confirmed before. */
  alreadyConfirmed: z.boolean(),
});
export type ExpenseReceiptConfirmResponse = z.infer<typeof expenseReceiptConfirmResponseSchema>;

const DECISION_REASON_MAX = 500;
const decisionReasonSchema = z.string().trim().min(1, 'Give a reason').max(DECISION_REASON_MAX);

/** `POST /expenses/:id/reject`: the reason is what the person who entered it reads. */
export const rejectExpenseInputSchema = z.object({ reason: decisionReasonSchema });
export type RejectExpenseInput = z.output<typeof rejectExpenseInputSchema>;

/**
 * `POST /expenses/:id/exception`: approving spend that has no receipt. It is an override, so a second person holding `override.approve`
 * (`overrideApproverId`, never the caller) signs it off beside the reason.
 */
export const exceptionExpenseInputSchema = z.object({ reason: decisionReasonSchema, overrideApproverId: z.uuid() });
export type ExceptionExpenseInput = z.output<typeof exceptionExpenseInputSchema>;

/** `awaiting_my_approval` is the approver's queue: submitted, inside the caller's ladder ranges, never one they entered themselves. */
export const EXPENSE_LIST_SCOPES = ['awaiting_my_approval'] as const;

/** The longest age filter: a year of hours. */
export const MAX_EXPENSE_AGE_HOURS = 8760;

/** `GET /expenses` — search matches the driver's name, the truck's registration, the station and the note. `minAgeHours` is time since the server wrote it. */
export const expenseListQuerySchema = defineListQuery({
  sortFields: ['deviceTime', 'serverTime', 'amount', 'category'],
  defaultSort: { sort: 'deviceTime', order: 'desc' },
  filters: {
    scope: z.enum(EXPENSE_LIST_SCOPES).optional(),
    minAgeHours: z.coerce.number().int().min(1).max(MAX_EXPENSE_AGE_HOURS).optional(),
    category: listFilter(expenseCategoryCodeSchema),
    state: listFilter(z.enum(EXPENSE_STATES)),
    driverId: listFilter(z.uuid()),
    tripId: listFilter(z.uuid()),
    truckId: listFilter(z.uuid()),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
  },
});
export type ExpenseListQuery = z.infer<typeof expenseListQuerySchema>;

export interface ExpenseCategoryView {
  code: string;
  name: string;
  /** A category that takes litres, station and odometer. */
  capturesFuelDetails: boolean;
}

export interface ExpenseReceiptView {
  id: string;
  kind: ExpenseReceiptKind;
  objectKey: string;
  contentType: string;
  sizeBytes: number;
}

export interface ExpenseView {
  id: string;
  driverId: string;
  driverName: string;
  tripId: string | null;
  truckId: string | null;
  /** The trip's `T-<n>` number when the expense names a trip, so an approver sees which trip it belongs to. */
  tripNumber: string | null;
  /** The truck's registration when the expense names a truck; a trip expense shows its trip instead. */
  truckRegistration: string | null;
  category: ExpenseCategoryView;
  amount: z.infer<typeof moneySchema>;
  note: string | null;
  fuel: ExpenseFuelDetails | null;
  receipts: ExpenseReceiptView[];
  state: ExpenseState;
  /** What the phone's clock said (or the office entered) and when the server wrote it. */
  deviceTime: string;
  serverTime: string;
  /** Null when the driver captured it; the office user who entered it for them otherwise. */
  capturedByUserId: string | null;
}
